import { CheckCircleOutlined, DollarOutlined, PercentageOutlined, EditOutlined, ExclamationCircleFilled, EyeOutlined, SettingOutlined, UserOutlined, TeamOutlined, CheckCircleFilled, CloseCircleFilled, UploadOutlined, SwapOutlined } from '@ant-design/icons';
import { Button, Form, Input, Modal, Table, Tag, Card, Space, Typography, Badge, Tooltip, Divider, Select, Alert } from 'antd';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../../../components/Footer';
import NavBar from '../../../components/NavBar';
import SideBar from '../../../components/SideBar';
import { peutModifierRetenueFiscale, rightsService } from '../../../services/rightsService';
import RetenueFiscaleModal from '../retenue/RetenueFiscaleModal';
import AddIntermediaire from '../AddIntermediaire';
import EditIntermediaire from '../EditIntermediaire';
import axiosInstance from '../../../variables/api/axiosInstance';
import { LOGGED_USER } from '../../../variables/constants';
import ActionDropdown from '../../production/components/ActionDropdown';
import { useNavigate } from 'react-router-dom';
import OssanActionDropdown from '../../../components/tables/OssanActionDropdown';
import { StatCard } from '../../../components/statistics/StatCard';
import ImportIntermediairesModal from './ImportIntermediairesModal';
import { searchIntermediaire } from '../../../hooks/useSearch';

const { confirm } = Modal;
const { Title, Text } = Typography;
const { Option } = Select;

// Libellés affichables des types d'intermédiaire (cohérents avec Add/EditIntermediaire).
const INTERMEDIAIRE_TYPE_LABELS = {
    AGENT_GENERAL: "Agent général",
    APPORTEUR_REGULIER: "Apporteur régulier",
    APPORTEUR_OCCASIONNEL: "Apporteur occasionnel",
    COURTIER: "Courtier",
};

// Message affiché lorsque la sélection contient des types hétérogènes.
const MIXED_TYPES_MESSAGE = "Impossible d'effectuer un changement groupé de type. La sélection contient des intermédiaires de types différents. Veuillez sélectionner uniquement des intermédiaires ayant le même type.";

const typeLabel = (type) => INTERMEDIAIRE_TYPE_LABELS[type] || type;

const Intermediaires = () => {
    const [intermediaires, setIntermediaires] = useState([]);
    const [selectedIntermediaire, setSelectedIntermediaire] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [reload, setReload] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [commissionData, setCommissionData] = useState([]);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [allCategories, setAllCategories] = useState([]);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [retenueModalOpen, setRetenueModalOpen] = useState(false);
    // Lignes cochées sur la page, reprises cochées dans la fenêtre du taux de retenue.
    const [retenuePreselection, setRetenuePreselection] = useState([]);

    // --- Pagination / tri côté serveur ---
    const [tableLoading, setTableLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
    const [sortBy, setSortBy] = useState("nom");
    const [sortDir, setSortDir] = useState("asc");
    const [stats, setStats] = useState({ total: 0, actifs: 0, inactifs: 0 });

    // --- Filtre par compagnie (POOL uniquement) ---
    // Une compagnie ne voit que ses propres intermédiaires : le filtre n'a de sens que pour le
    // Pool, qui voit tout. Le backend force de toute façon le périmètre pour une compagnie.
    const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
    const [compagnies, setCompagnies] = useState([]);
    const [codeOrganisme, setCodeOrganisme] = useState(null);

    // --- Recherche globale du header (NavBar) ---
    const [searchValue, setSearchValue] = useState("");
    const [searchResults, setSearchResults] = useState([]);

    // --- Bulk selection state ---
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);

    // --- Changement groupé de type ---
    const [typeModalVisible, setTypeModalVisible] = useState(false);
    const [typeChangeLoading, setTypeChangeLoading] = useState(false);

    const [form] = Form.useForm();
    const [typeForm] = Form.useForm();

    // Thème de couleurs
    const colors = {
        primary: '#242166',
        secondary: '#1890ff',
        success: '#52c41a',
        warning: '#faad14',
        error: '#ff4d4f',
        gradient: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
        cardBg: '#ffffff',
        headerBg: '#fafafa'
    };

    const handleShow = () => setShowModal(true);
    const handleClose = () => setShowModal(false);
    const handleEditClose = () => setShowEditModal(false);

    // Après édition : réactualiser UNIQUEMENT la ligne concernée (pas de rechargement de page ni
    // de refetch complet). L'historisation change le codeId → on remplace la ligne dont le codeId
    // == ancien codeId par la version mise à jour (merge pour conserver d'éventuels champs du DTO liste).
    const handleIntermediaireUpdated = (updated, oldCodeId) => {
        if (!updated) return;
        setIntermediaires((prev) =>
            prev.map((it) => (it.codeId === (oldCodeId || updated.codeId) ? { ...it, ...updated } : it))
        );
    };
    const handleOpenImport = () => setImportModalOpen(true);
    const handleCloseImport = () => setImportModalOpen(false);

    const activateIntermediaireConfirm = (inter) => {
        confirm({
            title: `Etes-vous sûr de vouloir ${inter.active ? "désactiver" : "activer"} cet intermédiaire?`,
            icon: <ExclamationCircleFilled style={{ color: colors.warning }} />,
            content: "Toutes vos actions sont historisées!",
            okText: "Oui",
            okType: "danger",
            cancelText: "Annuler",
            okButtonProps: {
                style: {
                    background: inter.active ? colors.error : colors.success,
                    borderColor: inter.active ? colors.error : colors.success
                }
            },
            onOk() {
                activateIntermediaire(inter);
            },
        });
    };

    const activateIntermediaire = (inter) => {
        axiosInstance
            .get(`/api/intermediaire/activity/${inter.codeIntermediaire}`).then((res) => {
                inter.active ? toast("Intermédiaire désactivé avec succès!") : toast("Intermédiaire activé avec succès!");
                setReload(prev => !prev);
            }).catch(err => {
                console.log(err);
                inter.active ? toast("Erreur lors de la désactivation de l'intermédiaire") : toast("Erreur lors de l'activation de l'intermédiaire");
            });
    }

    const handleEditShow = (inter) => {
        setSelectedIntermediaire(inter);
        setShowEditModal(true);
    }

    // Récupère uniquement la page demandée (pagination + tri serveur).
    const fetchIntermediaires = () => {
        setTableLoading(true);
        axiosInstance
            .get(`/api/intermediaire/page`, {
                params: {
                    page: (pagination.current || 1) - 1,
                    size: pagination.pageSize || 10,
                    sortBy,
                    sortDir,
                    // Filtre compagnie : ignoré côté serveur si l'utilisateur n'est pas le Pool.
                    ...(codeOrganisme ? { codeOrganisme } : {}),
                },
            })
            .then((res) => {
                setIntermediaires(res.data?.content || []);
                setPagination((prev) => ({ ...prev, total: res.data?.totalElements || 0 }));
                setSelectedRowKeys([]); // reset selection on reload
                setTableLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setTableLoading(false);
                toast.error("Erreur lors du chargement des intermédiaires");
            });
    };

    // Statistiques (total / actifs / inactifs) calculées côté serveur.
    const fetchStats = () => {
        axiosInstance
            // Mêmes filtres que la liste, sinon les cartes contrediraient le tableau.
            .get(`/api/intermediaire/stats`, {
                params: { ...(codeOrganisme ? { codeOrganisme } : {}) },
            })
            .then((res) => {
                setStats(res.data || { total: 0, actifs: 0, inactifs: 0 });
            })
            .catch((err) => {
                console.log(err);
            });
    };

    // Rechargement de la page de données à chaque changement de page, taille,
    // tri, ou après une action (ajout / activation...).
    useEffect(() => {
        fetchIntermediaires();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.current, pagination.pageSize, sortBy, sortDir, showModal, reload, codeOrganisme]);

    // Les statistiques ne dépendent pas de la pagination, mais bien du filtre compagnie.
    useEffect(() => {
        fetchStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal, reload, codeOrganisme]);

    // Liste des compagnies pour le filtre (Pool uniquement).
    useEffect(() => {
        if (!isPool) return;
        axiosInstance.get(`/api/organisme/assurance`)
            .then((res) => setCompagnies(res.data || []))
            .catch((err) => console.log(err));
    }, [isPool]);

    useEffect(() => {
        axiosInstance.get(`/api/categorie`).then((res) => {
            setAllCategories(res.data?.filter((c) => !c.codeCategorie?.startsWith("V")));
        }).catch((err) => {
            console.log(err);
        });
    }, []);

    // Recherche globale du header (NavBar) : interroge le serveur dès 2+
    // caractères et alimente le menu déroulant de suggestions.
    useEffect(() => {
        if (searchValue && searchValue.trim().length > 2) {
            searchIntermediaire(searchValue.trim())
                .then((res) => setSearchResults(res.data?.content || []))
                .catch((err) => console.log(err));
        } else {
            setSearchResults([]);
        }
    }, [searchValue]);

    // Rendu des suggestions de la recherche header.
    const renderSearchResults = (results) => (
        <div className="px-3 py-2">
            {results.map((item, index) => (
                <a
                    key={index}
                    href={`/intermediaire/${item.codeId}`}
                    className="dropdown-item d-flex align-items-center gap-2 py-1"
                >
                    <span className="text-truncate">
                        {item?.codeIntermediaire} — {item?.personne?.nom} {item?.personne?.prenom}
                    </span>
                </a>
            ))}
        </div>
    );

    // Gestion du changement de page / taille / tri depuis le tableau Ant Design.
    const handleTableChange = (paginationArg, _filters, sorter) => {
        setPagination((prev) => ({
            ...prev,
            current: paginationArg.current,
            pageSize: paginationArg.pageSize,
        }));

        const sortFieldMap = {
            nom: "nom",
            codeIntermediaire: "code",
            type: "type",
            active: "active",
        };
        if (sorter && sorter.order) {
            setSortBy(sortFieldMap[sorter.field] || "nom");
            setSortDir(sorter.order === "descend" ? "desc" : "asc");
        } else {
            // Retour au tri par défaut : nom croissant.
            setSortBy("nom");
            setSortDir("asc");
        }
    };

    const showCommissionsModal = (record) => {
        setSelectedIntermediaire(record);

        const existingCommissions = new Map(
            record.tauxCommission?.map(comm => [comm?.categorie?.codeCategorie, comm.taux])
        );

        const initialFormValues = {
            commissions: allCategories.map(cat => ({
                categorie: cat,
                taux: existingCommissions.get(cat.codeCategorie) || 0,
            }))
        };

        setCommissionData(initialFormValues.commissions);
        form.setFieldsValue(initialFormValues);
        setIsModalVisible(true);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
        setSelectedIntermediaire(null);
        setCommissionData([]);
    };

    const handleSave = (values) => {
        setLoading(true);
        axiosInstance.put(`/api/intermediaire/taux-commission`, {
            intermediaireId: selectedIntermediaire.codeId,
            commissions: values.commissions
        }).then(res => {
            toast.success("Taux de commission mis à jour avec succès!");
            setReload(prev => !prev);
            handleCancel();
            setLoading(false);
        }).catch(err => {
            console.log(err);
            toast.error("Erreur lors de la mise à jour des taux de commission");
            handleCancel();
            setLoading(false);
        });
    };

    // ─── Bulk action helpers ──────────────────────────────────────────────────

    /**
     * Executes a toggle request for a list of intermediaires codes.
     * Only calls the API for records whose current `active` status differs
     * from the desired `targetActive` value (avoids unnecessary calls).
     */
    const executeBulkToggle = (codes, targetActive) => {
        setBulkLoading(true);
        const requests = codes.map((code) =>
            axiosInstance.get(`/api/intermediaire/activity/${code}`)
        );
        Promise.allSettled(requests).then((results) => {
            const failed = results.filter(r => r.status === "rejected").length;
            const succeeded = results.filter(r => r.status === "fulfilled").length;
            if (succeeded > 0) {
                toast.success(
                    `${succeeded} intermédiaire(s) ${targetActive ? "activé(s)" : "désactivé(s)"} avec succès!`
                );
            }
            if (failed > 0) {
                toast.error(`${failed} intermédiaire(s) n'ont pas pu être traité(s).`);
            }
            setSelectedRowKeys([]);
            setReload(prev => !prev);
            setBulkLoading(false);
        });
    };

    /**
     * Shows a confirm dialog then triggers the bulk toggle.
     * @param {'activate'|'deactivate'} action
     * @param {Array}  targets  – array of intermediaire objects to act on
     */
    const confirmBulkAction = (action, targets) => {
        if (!targets.length) return;
        const isActivating = action === 'activate';
        const verb = isActivating ? "activer" : "désactiver";
        const count = targets.length;
        const label = count === intermediaires.length ? "tous les intermédiaires de cette page" : `${count} intermédiaire(s) sélectionné(s)`;

        confirm({
            title: `Confirmer l'action groupée`,
            icon: <ExclamationCircleFilled style={{ color: colors.warning }} />,
            content: (
                <span>
                    Êtes-vous sûr de vouloir <strong>{verb}</strong> {label} ?
                    <br />
                    <Text type="secondary" style={{ fontSize: 12 }}>Toutes vos actions sont historisées.</Text>
                </span>
            ),
            okText: `Oui, ${verb}`,
            okType: "danger",
            cancelText: "Annuler",
            okButtonProps: {
                style: {
                    background: isActivating ? colors.success : colors.error,
                    color: "white",
                    borderColor: isActivating ? colors.success : colors.error
                }
            },
            onOk() {
                // Only toggle records that need to change state
                const toChange = targets.filter(i => i.active !== isActivating);
                if (!toChange.length) {
                    toast.info("Aucun changement nécessaire, tous les intermédiaires sont déjà dans l'état souhaité.");
                    return;
                }
                executeBulkToggle(toChange.map(i => i.codeIntermediaire), isActivating);
            }
        });
    };

    // Derived helpers
    const selectedObjects = intermediaires.filter(i => selectedRowKeys.includes(i.codeId));
    const hasSelection = selectedRowKeys.length > 0;

    // ─── Changement groupé de type ────────────────────────────────────────────

    // Types distincts présents dans la sélection.
    const selectedTypes = [...new Set(selectedObjects.map(i => i.type))];
    // La sélection est homogène lorsque tous les intermédiaires partagent le même type.
    const isHomogeneousType = hasSelection && selectedTypes.length === 1;
    const commonType = isHomogeneousType ? selectedTypes[0] : null;

    /**
     * Ouvre la modale de changement de type après contrôle d'homogénéité.
     * Si la sélection contient des types différents, l'opération est bloquée
     * et un message explicite est affiché.
     */
    const openTypeChangeModal = () => {
        if (!hasSelection) return;
        if (!isHomogeneousType) {
            Modal.warning({
                title: "Changement groupé de type impossible",
                icon: <ExclamationCircleFilled style={{ color: colors.warning }} />,
                content: MIXED_TYPES_MESSAGE,
                okText: "J'ai compris",
            });
            return;
        }
        typeForm.setFieldsValue({ type: commonType });
        setTypeModalVisible(true);
    };

    const handleTypeChangeCancel = () => {
        setTypeModalVisible(false);
        typeForm.resetFields();
    };

    /**
     * Applique le nouveau type à tous les intermédiaires sélectionnés.
     * Chaque mise à jour passe par PUT /api/intermediaire (historisé côté
     * serveur), garantissant la traçabilité de la modification groupée.
     */
    const executeBulkTypeChange = async (values) => {
        const newType = values.type;
        // Re-contrôle d'homogénéité avant écriture (sécurité).
        if (!isHomogeneousType) {
            toast.error(MIXED_TYPES_MESSAGE);
            handleTypeChangeCancel();
            return;
        }
        if (newType === commonType) {
            toast.info("Le type sélectionné est identique au type actuel. Aucun changement effectué.");
            handleTypeChangeCancel();
            return;
        }

        setTypeChangeLoading(true);
        const targets = selectedObjects;
        let succeeded = 0;
        const failures = [];

        // Exécution SÉQUENTIELLE volontaire : le backend historise chaque mise à jour
        // avec un identifiant calculé en mémoire (lastHistId + 1) et re-lie les contrats.
        // Des PUT parallèles entrent en collision sur cet identifiant (erreur 500).
        // En séquentiel, chaque requête commit avant que la suivante ne lise le compteur.
        for (const inter of targets) {
            try {
                // On conserve l'intégralité de l'objet et on n'écrase que le type,
                // afin de préserver les contrôles métier existants sur les autres champs.
                await axiosInstance.put(`/api/intermediaire`, JSON.stringify({ ...inter, type: newType }));
                succeeded += 1;
            } catch (err) {
                const data = err?.response?.data;
                const reason = (typeof data === "string" ? data : data?.message)
                    || err?.message
                    || "Erreur inconnue";
                const nom = `${inter?.personne?.nom || ""} ${inter?.personne?.prenom || ""}`.trim();
                failures.push({ code: inter?.codeIntermediaire, nom, reason });
            }
        }

        if (succeeded > 0) {
            toast.success(
                `Type modifié en « ${typeLabel(newType)} » pour ${succeeded} intermédiaire(s) avec succès!`
            );
        }
        if (failures.length > 0) {
            console.warn("Changement de type — échecs :", failures);
            Modal.warning({
                title: `${failures.length} intermédiaire(s) n'ont pas pu être modifié(s)`,
                icon: <ExclamationCircleFilled style={{ color: colors.warning }} />,
                width: 560,
                content: (
                    <div style={{ maxHeight: 280, overflowY: "auto", marginTop: 8 }}>
                        {failures.map((f, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                                <Text strong>{f.code}{f.nom ? ` — ${f.nom}` : ""}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: 12 }}>{f.reason}</Text>
                            </div>
                        ))}
                    </div>
                ),
                okText: "Fermer",
            });
        }
        setTypeChangeLoading(false);
        setSelectedRowKeys([]);
        handleTypeChangeCancel();
        setReload(prev => !prev);
    };

    // ─────────────────────────────────────────────────────────────────────────

    const commissionColumns = [
        {
            title: <Text strong style={{ color: colors.primary }}>Catégorie</Text>,
            dataIndex: 'categorie',
            key: 'categorie',
            render: (text) => <Text>{text.nom}</Text>,
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Taux de Commission (%)</Text>,
            dataIndex: 'taux',
            key: 'taux',
            render: (text, record, index) => (
                <>
                    <Form.Item
                        name={['commissions', index, 'taux']}
                        initialValue={text}
                        rules={[{ required: true, message: 'Le taux est requis!' }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Input
                            type="number"
                            step="0.01"
                            style={{
                                borderColor: colors.primary,
                                borderRadius: '6px'
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name={['commissions', index, 'categorie']}
                        style={{ display: 'none' }}
                    >
                        <Input type="hidden" />
                    </Form.Item>
                </>
            ),
        },
    ];

    const navigate = useNavigate();
    const canCreateIntermediaire = rightsService("HLA052");

    const columns = [
        {
            title: <Text strong style={{ color: colors.primary }}>Code</Text>,
            dataIndex: "codeIntermediaire",
            key: "codeIntermediaire",
            sorter: true,
            render: (text) => (
                <Tag style={{
                    background: colors.gradient,
                    color: 'white',
                    border: 'none',
                    fontWeight: '500',
                    padding: '4px 12px',
                    borderRadius: '6px'
                }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Type</Text>,
            dataIndex: "type",
            key: "type",
            render: (text) => (
                <Badge
                    color={colors.secondary}
                    text={<Text style={{ color: '#595959' }}>{text}</Text>}
                />
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Nom Complet</Text>,
            dataIndex: "nom",
            key: "nom",
            sorter: true,
            defaultSortOrder: "ascend",
            render: (_, record) => (
                <Space>
                    <UserOutlined style={{ color: colors.primary }} />
                    <Text strong style={{ color: '#262626' }}>
                        {record?.personne?.nom} {record?.personne?.prenom}
                    </Text>
                </Space>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Contacts</Text>,
            dataIndex: "contacts",
            key: "contacts",
            render: (_, record) => (
                <Text style={{ color: '#595959' }}>
                    {record.personne?.contact && record.personne?.contact[0]}
                </Text>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Statut</Text>,
            dataIndex: "active",
            key: "active",
            render: (_, record) => (
                <Tag
                    icon={record.active ? <CheckCircleFilled /> : <CloseCircleFilled />}
                    color={record.active ? "success" : "error"}
                    style={{
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontWeight: '500'
                    }}
                >
                    {record.active ? "Actif" : "Inactif"}
                </Tag>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Actions</Text>,
            dataIndex: "action",
            key: "action",
            render: (_, record) => {
                const actions = [
                    {
                        key: "details",
                        label: "Détails",
                        icon: <EyeOutlined />,
                        onClick: (e) => {
                            navigate(`/intermediaire/${record.codeId}`);
                        },
                    },
                    {
                        key: "commissions",
                        label: "Commissions",
                        icon: <DollarOutlined />,
                        onClick: (info) => {
                            info?.domEvent?.stopPropagation?.();
                            showCommissionsModal(record);
                        },
                    },
                    rightsService("HPE041") &&
                    record.active && {
                        key: "edit",
                        label: "Modifier",
                        icon: <EditOutlined />,
                        onClick: (info) => {
                            info?.domEvent?.preventDefault?.();
                            info?.domEvent?.stopPropagation?.();
                            handleEditShow(record);
                        },
                    },
                    rightsService("HPE041") && {
                        key: "reset",
                        label: `${record?.active ? "Désactiver" : "Activer"}`,
                        icon: <CheckCircleOutlined />,
                        onClick: (info) => {
                            info?.domEvent?.preventDefault?.();
                            info?.domEvent?.stopPropagation?.();
                            activateIntermediaireConfirm(record)
                        },
                    },
                ].filter(Boolean);

                return <OssanActionDropdown actions={actions} />;
            }
        },
    ];

    const poolColumns = [
        {
            title: <Text strong style={{ color: colors.primary }}>Code</Text>,
            dataIndex: "codeIntermediaire",
            key: "codeIntermediaire",
            sorter: true,
            render: (text) => (
                <Tag style={{
                    background: colors.gradient,
                    color: 'white',
                    border: 'none',
                    fontWeight: '500',
                    padding: '4px 12px',
                    borderRadius: '6px'
                }}>
                    {text}
                </Tag>
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Type</Text>,
            dataIndex: "type",
            key: "type",
            render: (text) => (
                <Badge
                    color={colors.secondary}
                    text={<Text style={{ color: '#595959' }}>{text}</Text>}
                />
            ),
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Nom</Text>,
            dataIndex: "nom",
            key: "nom",
            sorter: true,
            defaultSortOrder: "ascend",
            render: (_, record) => (
                <Space>
                    <UserOutlined style={{ color: colors.primary }} />
                    <Text strong style={{ color: '#262626' }}>
                        {record.personne?.nom} {record.personne?.prenom}
                    </Text>
                </Space>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Organisme</Text>,
            dataIndex: "organisme",
            key: "organisme",
            render: (_, record) => (
                <Text style={{ color: '#595959' }}>
                    {record?.organisme?.raisonSociale || "-"}
                </Text>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Contacts</Text>,
            dataIndex: "contacts",
            key: "contacts",
            render: (_, record) => (
                <Text style={{ color: '#595959' }}>
                    {record.contact && record.contact[0]}
                </Text>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Statut</Text>,
            dataIndex: "active",
            key: "active",
            render: (_, record) => (
                <Tag
                    icon={record.active ? <CheckCircleFilled /> : <CloseCircleFilled />}
                    color={record.active ? "success" : "error"}
                    style={{
                        borderRadius: '6px',
                        padding: '4px 12px',
                        fontWeight: '500'
                    }}
                >
                    {record.active ? "Actif" : "Inactif"}
                </Tag>
            )
        },
        {
            title: <Text strong style={{ color: colors.primary }}>Actions</Text>,
            dataIndex: "action",
            key: "action",
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        shape="circle"
                        onClick={() => handleEditShow(record)}
                        style={{
                            background: colors.secondary,
                            borderColor: colors.secondary
                        }}
                    />
                    <Button
                        type="primary"
                        icon={record?.active ? <CloseCircleFilled /> : <CheckCircleFilled />}
                        shape="circle"
                        onClick={() => activateIntermediaireConfirm(record)}
                        style={{
                            background: !record?.active ? colors.success : colors.error,
                            borderColor: !record?.active ? colors.success : colors.error
                        }}
                    />
                </Space>
            )
        },
    ];

    // Row selection config (uses codeId as the row key)
    const rowSelection = rightsService("HPE041") ? {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
        getCheckboxProps: (record) => ({
            name: record.codeIntermediaire,
        }),
    } : undefined;

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"COURTIERS"} />

                <div className="layout-page">
                    <NavBar
                        filteredResults={searchResults}
                        setFilteredResults={setSearchResults}
                        renderSearchResults={renderSearchResults}
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                    />

                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            {/* Header Section */}
                            <div style={{ marginBottom: '24px' }}>
                                <Title level={2} style={{
                                    background: colors.gradient,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    marginBottom: '8px'
                                }}>
                                    Gestion des Intermédiaires
                                </Title>
                                <Text style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                    Gérez et suivez tous vos intermédiaires en un seul endroit
                                </Text>
                            </div>

                            {/* Statistics Cards */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                                gap: '20px',
                                marginBottom: '24px'
                            }}>
                                <StatCard
                                    title="Total Intermédiaires"
                                    value={stats.total}
                                    subtitle="Intermédiaires enregistrés"
                                    icon="bx bx-group"
                                    iconBg={colors.gradient}
                                    gradient={colors.gradient}
                                />
                                <StatCard
                                    title="Intermédiaires Actifs"
                                    value={stats.actifs}
                                    subtitle="Actuellement en service"
                                    icon="bx bx-check-circle"
                                    iconBg="linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                                    gradient="linear-gradient(135deg, #52c41a 0%, #73d13d 100%)"
                                />
                                <StatCard
                                    title="Intermédiaires Inactifs"
                                    value={stats.inactifs}
                                    subtitle="Temporairement désactivés"
                                    icon="bx bx-x-circle"
                                    iconBg="linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)"
                                    gradient="linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)"
                                />
                            </div>

                            {/* Action Buttons */}
                            {rightsService("HLA052") && (
                                <div style={{ marginBottom: '20px' }}>
                                    <Space wrap>
                                        <Button
                                            type="primary"
                                            size="large"
                                            icon={<i className="bx bx-plus" style={{ fontSize: '18px' }}></i>}
                                            onClick={handleShow}
                                            disabled={LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL"}
                                            style={{
                                                background: colors.gradient,
                                                border: 'none',
                                                height: '44px',
                                                borderRadius: '8px',
                                                fontWeight: '500',
                                                boxShadow: '0 4px 12px rgba(36, 33, 102, 0.2)'
                                            }}
                                        >
                                            Ajouter un intermédiaire
                                        </Button>
                                        {canCreateIntermediaire && (
                                            <Button
                                                icon={<UploadOutlined />}
                                                size="large"
                                                onClick={handleOpenImport}
                                                disabled={LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL"}
                                                style={{
                                                    borderRadius: '8px',
                                                    borderColor: colors.primary,
                                                    color: colors.primary,
                                                    height: '44px'
                                                }}
                                            >
                                                Importer (Excel)
                                            </Button>
                                        )}
                                        {peutModifierRetenueFiscale() && (
                                            <Button
                                                icon={<PercentageOutlined />}
                                                size="large"
                                                onClick={() => { setRetenuePreselection([]); setRetenueModalOpen(true); }}
                                                style={{
                                                    borderRadius: '8px',
                                                    borderColor: colors.primary,
                                                    color: colors.primary,
                                                    height: '44px'
                                                }}
                                            >
                                                Taux de retenue fiscale
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            )}

                            {/* ── Bulk action toolbar ── */}
                            {rightsService("HPE041") && (
                                <Card
                                    size="small"
                                    style={{
                                        marginBottom: '16px',
                                        borderRadius: '10px',
                                        border: `1px solid ${hasSelection ? colors.primary : '#f0f0f0'}`,
                                        background: hasSelection ? '#f5f5ff' : '#fafafa',
                                        transition: 'all 0.3s ease'
                                    }}
                                    bodyStyle={{ padding: '12px 16px' }}
                                >
                                    <Space wrap align="center">
                                        <Text strong style={{ color: colors.primary, fontSize: 13 }}>
                                            Actions groupées :
                                        </Text>

                                        {/* ── "Tout" actions (page courante) ── */}
                                        <Tooltip title="Activer tous les intermédiaires affichés sur cette page">
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                icon={<CheckCircleFilled style={{ color: colors.success }} />}
                                                onClick={() => confirmBulkAction('activate', intermediaires)}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Activer la page
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title="Désactiver tous les intermédiaires affichés sur cette page">
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                icon={<CloseCircleFilled style={{ color: colors.error }} />}
                                                onClick={() => confirmBulkAction('deactivate', intermediaires)}
                                                style={{ borderRadius: '6px' }}
                                            >
                                                Désactiver la page
                                            </Button>
                                        </Tooltip>

                                        <Divider type="vertical" style={{ height: 24, borderColor: '#d9d9d9' }} />

                                        {/* ── Selection actions ── */}
                                        <Tooltip title={!hasSelection ? "Cochez d'abord des lignes dans le tableau" : `Activer les ${selectedRowKeys.length} intermédiaire(s) sélectionné(s)`}>
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                disabled={!hasSelection}
                                                icon={<CheckCircleFilled />}
                                                onClick={() => confirmBulkAction('activate', selectedObjects)}
                                                style={{
                                                    borderRadius: '6px',
                                                    background: hasSelection ? colors.success : undefined,
                                                    borderColor: hasSelection ? colors.success : undefined,
                                                    color: hasSelection ? '#fff' : undefined,
                                                    fontWeight: hasSelection ? '500' : undefined
                                                }}
                                            >
                                                Activer la sélection
                                                {hasSelection && (
                                                    <Tag
                                                        color="white"
                                                        style={{
                                                            color: colors.success,
                                                            marginLeft: 6,
                                                            borderRadius: '10px',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            lineHeight: '18px',
                                                            padding: '0 7px'
                                                        }}
                                                    >
                                                        {selectedRowKeys.length}
                                                    </Tag>
                                                )}
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title={!hasSelection ? "Cochez d'abord des lignes dans le tableau" : `Désactiver les ${selectedRowKeys.length} intermédiaire(s) sélectionné(s)`}>
                                            <Button
                                                size="middle"
                                                loading={bulkLoading}
                                                disabled={!hasSelection}
                                                icon={<CloseCircleFilled />}
                                                onClick={() => confirmBulkAction('deactivate', selectedObjects)}
                                                style={{
                                                    borderRadius: '6px',
                                                    background: hasSelection ? colors.error : undefined,
                                                    borderColor: hasSelection ? colors.error : undefined,
                                                    color: hasSelection ? '#fff' : undefined,
                                                    fontWeight: hasSelection ? '500' : undefined
                                                }}
                                            >
                                                Désactiver la sélection
                                                {hasSelection && (
                                                    <Tag
                                                        color="white"
                                                        style={{
                                                            color: colors.error,
                                                            marginLeft: 6,
                                                            borderRadius: '10px',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            lineHeight: '18px',
                                                            padding: '0 7px'
                                                        }}
                                                    >
                                                        {selectedRowKeys.length}
                                                    </Tag>
                                                )}
                                            </Button>
                                        </Tooltip>

                                        <Divider type="vertical" style={{ height: 24, borderColor: '#d9d9d9' }} />

                                        {/* ── Changement groupé de type ── */}
                                        <Tooltip
                                            title={
                                                !hasSelection
                                                    ? "Cochez d'abord des lignes dans le tableau"
                                                    : !isHomogeneousType
                                                        ? MIXED_TYPES_MESSAGE
                                                        : `Changer le type des ${selectedRowKeys.length} intermédiaire(s) sélectionné(s)`
                                            }
                                        >
                                            <Button
                                                size="middle"
                                                loading={typeChangeLoading}
                                                disabled={!hasSelection || !isHomogeneousType}
                                                icon={<SwapOutlined />}
                                                onClick={openTypeChangeModal}
                                                style={{
                                                    borderRadius: '6px',
                                                    background: (hasSelection && isHomogeneousType) ? colors.primary : undefined,
                                                    borderColor: (hasSelection && isHomogeneousType) ? colors.primary : undefined,
                                                    color: (hasSelection && isHomogeneousType) ? '#fff' : undefined,
                                                    fontWeight: (hasSelection && isHomogeneousType) ? '500' : undefined
                                                }}
                                            >
                                                Changer le type
                                                {isHomogeneousType && (
                                                    <Tag
                                                        color="white"
                                                        style={{
                                                            color: colors.primary,
                                                            marginLeft: 6,
                                                            borderRadius: '10px',
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            lineHeight: '18px',
                                                            padding: '0 7px'
                                                        }}
                                                    >
                                                        {typeLabel(commonType)}
                                                    </Tag>
                                                )}
                                            </Button>
                                        </Tooltip>

                                        {peutModifierRetenueFiscale() && (
                                            <Tooltip title={!hasSelection ? "Cochez d'abord des lignes dans le tableau" : `Changer le taux de retenue des ${selectedRowKeys.length} intermédiaire(s) coché(s)`}>
                                                <Button
                                                    size="middle"
                                                    disabled={!hasSelection}
                                                    icon={<PercentageOutlined />}
                                                    onClick={() => { setRetenuePreselection(selectedRowKeys); setRetenueModalOpen(true); }}
                                                    style={{ borderRadius: '6px' }}
                                                >
                                                    Changer le taux de retenue
                                                </Button>
                                            </Tooltip>
                                        )}

                                        {hasSelection && (
                                            <Button
                                                size="middle"
                                                type="link"
                                                onClick={() => setSelectedRowKeys([])}
                                                style={{ color: '#8c8c8c', padding: '0 4px' }}
                                            >
                                                Désélectionner tout
                                            </Button>
                                        )}
                                    </Space>

                                    {/* Message explicite si la sélection contient des types différents */}
                                    {hasSelection && !isHomogeneousType && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            style={{ marginTop: 12, borderRadius: '8px' }}
                                            message="Changement groupé de type indisponible"
                                            description={MIXED_TYPES_MESSAGE}
                                        />
                                    )}
                                </Card>
                            )}

                            {/* Table Card */}
                            <Card
                                style={{
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #f0f0f0'
                                }}
                                bodyStyle={{ padding: 0 }}
                            >
                                {/* Filtre par compagnie — réservé au Pool (une compagnie ne voit
                                    que ses propres intermédiaires, le filtre serait sans objet). */}
                                {isPool && (
                                    <div style={{
                                        padding: "12px 16px", borderBottom: "1px solid #f0f0f0",
                                        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                                    }}>
                                        <span style={{ color: "#8c8c8c", fontSize: 13 }}>Compagnie :</span>
                                        <Select
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                            placeholder="Toutes les compagnies"
                                            style={{ minWidth: 280 }}
                                            value={codeOrganisme}
                                            onChange={(v) => {
                                                setCodeOrganisme(v || null);
                                                // Retour en page 1 : rester sur la page courante
                                                // afficherait une page vide sur un périmètre réduit.
                                                setPagination((p) => ({ ...p, current: 1 }));
                                            }}
                                            options={compagnies.map((c) => ({
                                                value: c.code,
                                                label: c.raisonSociale || c.code,
                                            }))}
                                        />
                                        {codeOrganisme && (
                                            <Tag color="blue">{stats.total} intermédiaire(s)</Tag>
                                        )}
                                    </div>
                                )}
                                <Table
                                    rowKey="codeId"
                                    loading={tableLoading}
                                    rowSelection={rowSelection}
                                    columns={LOGGED_USER.profil?.organisme?.typeOrganisme === "POOL" ? poolColumns : columns}
                                    dataSource={intermediaires}
                                    onChange={handleTableChange}
                                    onRow={(record) => ({
                                        onClick: (e) => {
                                            // Clic sur la ligne → page de détails de l'intermédiaire.
                                            // Garde : on n'y va PAS si le clic vient d'un contrôle
                                            // (case à cocher, menu d'actions, bouton, lien, select…).
                                            if (e.target.closest(
                                                'button, a, input, .ant-checkbox-wrapper, .ant-dropdown-trigger, .ant-switch, .ant-select, .ant-tag'
                                            )) {
                                                return;
                                            }
                                            navigate(`/intermediaire/${record.codeId}`);
                                        },
                                        style: { cursor: 'pointer' }
                                    })}
                                    pagination={{
                                        current: pagination.current,
                                        pageSize: pagination.pageSize,
                                        total: pagination.total,
                                        showSizeChanger: true,
                                        pageSizeOptions: ['10', '20', '50', '100'],
                                        showTotal: (total) => `Total: ${total} intermédiaires`,
                                        style: { padding: '16px' }
                                    }}
                                    style={{
                                        borderRadius: '12px'
                                    }}
                                />
                            </Card>

                            {/* Commission Modal */}
                            <Modal
                                title={
                                    <div style={{
                                        padding: '8px 0',
                                        borderBottom: `2px solid ${colors.primary}`
                                    }}>
                                        <Title level={4} style={{
                                            margin: 0,
                                            color: colors.primary
                                        }}>
                                            <SettingOutlined style={{ marginRight: '8px' }} />
                                            Edition des taux de commissions
                                        </Title>
                                        <Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                                            {selectedIntermediaire?.personne?.nom} {selectedIntermediaire?.personne?.prenom}
                                        </Text>
                                    </div>
                                }
                                open={isModalVisible}
                                onCancel={handleCancel}
                                footer={null}
                                width={700}
                                style={{ top: 20 }}
                            >
                                {selectedIntermediaire && (
                                    <Form form={form} onFinish={handleSave}>
                                        <div style={{ margin: '20px 0' }}>
                                            <Table
                                                columns={commissionColumns}
                                                dataSource={commissionData}
                                                pagination={false}
                                                size="middle"
                                                style={{
                                                    border: '1px solid #f0f0f0',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </div>
                                        <div style={{
                                            marginTop: '24px',
                                            textAlign: 'right',
                                            paddingTop: '16px',
                                            borderTop: '1px solid #f0f0f0'
                                        }}>
                                            <Space>
                                                <Button
                                                    onClick={handleCancel}
                                                    size="large"
                                                    style={{
                                                        borderRadius: '6px',
                                                        minWidth: '100px'
                                                    }}
                                                >
                                                    Annuler
                                                </Button>
                                                <Button
                                                    loading={loading}
                                                    type="primary"
                                                    htmlType="submit"
                                                    size="large"
                                                    style={{
                                                        background: colors.gradient,
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        minWidth: '120px',
                                                        boxShadow: '0 2px 8px rgba(36, 33, 102, 0.2)'
                                                    }}
                                                >
                                                    Sauvegarder
                                                </Button>
                                            </Space>
                                        </div>
                                    </Form>
                                )}
                            </Modal>

                            {/* Modale de changement groupé de type */}
                            <Modal
                                title={
                                    <div style={{
                                        padding: '8px 0',
                                        borderBottom: `2px solid ${colors.primary}`
                                    }}>
                                        <Title level={4} style={{
                                            margin: 0,
                                            color: colors.primary
                                        }}>
                                            <SwapOutlined style={{ marginRight: '8px' }} />
                                            Changer le type des intermédiaires
                                        </Title>
                                        <Text style={{ color: '#8c8c8c', fontSize: '13px' }}>
                                            {selectedRowKeys.length} intermédiaire(s) sélectionné(s)
                                            {commonType && <> — type actuel : <strong>{typeLabel(commonType)}</strong></>}
                                        </Text>
                                    </div>
                                }
                                open={typeModalVisible}
                                onCancel={handleTypeChangeCancel}
                                footer={null}
                                width={520}
                                style={{ top: 40 }}
                            >
                                <Form form={typeForm} onFinish={executeBulkTypeChange} layout="vertical">
                                    <div style={{ margin: '20px 0' }}>
                                        <Form.Item
                                            name="type"
                                            label={<Text strong style={{ color: colors.primary }}>Nouveau type</Text>}
                                            rules={[{ required: true, message: "Veuillez sélectionner le nouveau type" }]}
                                        >
                                            <Select
                                                placeholder="Sélectionnez le nouveau type"
                                                size="large"
                                                suffixIcon={<TeamOutlined style={{ color: colors.secondary }} />}
                                                style={{ borderRadius: '8px' }}
                                            >
                                                <Option value="AGENT_GENERAL">Agent général</Option>
                                                <Option value="APPORTEUR_REGULIER">Apporteur régulier</Option>
                                                <Option value="APPORTEUR_OCCASIONNEL">Apporteur occasionnel</Option>
                                                <Option value="COURTIER">Courtier</Option>
                                            </Select>
                                        </Form.Item>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Le nouveau type sera appliqué à tous les intermédiaires sélectionnés.
                                            Cette opération groupée est historisée.
                                        </Text>
                                    </div>
                                    <div style={{
                                        marginTop: '24px',
                                        textAlign: 'right',
                                        paddingTop: '16px',
                                        borderTop: '1px solid #f0f0f0'
                                    }}>
                                        <Space>
                                            <Button
                                                onClick={handleTypeChangeCancel}
                                                size="large"
                                                style={{ borderRadius: '6px', minWidth: '100px' }}
                                            >
                                                Annuler
                                            </Button>
                                            <Button
                                                loading={typeChangeLoading}
                                                type="primary"
                                                htmlType="submit"
                                                size="large"
                                                style={{
                                                    background: colors.gradient,
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    minWidth: '120px',
                                                    boxShadow: '0 2px 8px rgba(36, 33, 102, 0.2)'
                                                }}
                                            >
                                                Confirmer
                                            </Button>
                                        </Space>
                                    </div>
                                </Form>
                            </Modal>
                        </div>

                        <Footer />
                        <div className="content-backdrop fade"></div>
                    </div>
                </div>
            </div>

            <AddIntermediaire
                show={showModal}
                handleClose={handleClose}
            />
            <EditIntermediaire
                show={showEditModal}
                handleClose={handleEditClose}
                intermediaire={selectedIntermediaire}
                onUpdated={handleIntermediaireUpdated}
            />
            <ImportIntermediairesModal
                open={importModalOpen && canCreateIntermediaire}
                onClose={handleCloseImport}
                colors={colors}
                onImported={() => setReload((prev) => !prev)}
            />
            <RetenueFiscaleModal
                nature="INTERMEDIAIRE"
                preselection={retenuePreselection}
                open={retenueModalOpen}
                onClose={() => setRetenueModalOpen(false)}
                onApplied={() => { setSelectedRowKeys([]); setReload((prev) => !prev); }}
            />

            <div className="layout-overlay layout-menu-toggle"></div>
            <div className="drag-target"></div>
        </div>
    );
}

export default Intermediaires;